import { HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import './styles/Draggable.scss';

import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { classNames, componentClassName } from '@dw/utils';

import { DragItem, DragItemType, DragItemTypes } from "./types";
import { useGlobalDragDropMonitor } from './useGlobalDragDropMonitor';
import { useDragAndDropContext } from './DragAndDropProvider';

/** Callback ref that marks the single element allowed to start a drag. */
export type ConnectDragHandle = (element: HTMLElement | null) => void;

interface IDraggableProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  dragData: T;
  /**
   * Row content.
   *
   * Pass a plain node and the WHOLE root div becomes the drag source (the original behaviour).
   * Pass a FUNCTION to take over where the drag starts: attach the supplied `connectDragHandle`
   * callback ref to one element and only that element begins a drag — which keeps row clicks and
   * text selection working everywhere else in the row.
   *
   * The function is invoked TWICE per render — once to build the (inert) drag preview and once for
   * the live row — so it must be a pure function of `dragData`/closed-over props and must NOT call
   * hooks.
   */
  children: ReactNode | ((connectDragHandle: ConnectDragHandle) => ReactNode);
  disabled?: boolean;
  /**
   * The react-dnd item type. Defaults to the shared `DragItemTypes.BOX`.
   *
   * Pass a value unique to one list so react-dnd never offers a card from a DIFFERENT list as a
   * valid drop — with a single global type every draggable in the app is interchangeable.
   */
  dragType?: DragItemType;
}
export function Draggable<T>(props: IDraggableProps<T>) {

  const {
    children,
    dragData,
    disabled,
    dragType,
    ...divBaseProps
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const { disabled: globalDisabled } = useDragAndDropContext();
  const { isDragging: isGlobalDragging } = useGlobalDragDropMonitor();

  const cn = componentClassName('draggable');

  const usesDragHandle = typeof children === 'function';
  const renderChildren = useCallback(
    (connect: ConnectDragHandle) => typeof children === 'function' ? children(connect) : children,
    [children]
  );

  const previewNode = useMemo(() => {
    if (!ref.current) return null;

    return (
      <div
        {...divBaseProps}
        className={classNames(cn(), cn('preview'), divBaseProps.className)}
        style={{
          ...divBaseProps.style,
          width: ref.current.clientWidth,
          height: ref.current.clientHeight
        }}
        draggable={false}
        aria-hidden
      >
        {/* The preview is inert, so connecting a handle inside it would be meaningless. */}
        {renderChildren(() => undefined)}
      </div>
    )
  }, [divBaseProps, renderChildren, cn]);

  const [{ isDragging }, dragRef, preview] = useDrag(() => ({
    type: dragType ?? DragItemTypes.BOX,
    item: {
      data: dragData,
      preview: previewNode
    } as DragItem<T>,
    canDrag: !disabled && !globalDisabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [previewNode, dragData, disabled, globalDisabled, dragType]);

  // Wrapped so the ref callback returns void: React 19 treats a non-void return as a cleanup fn.
  const connectDragHandle = useCallback<ConnectDragHandle>((element) => {
    dragRef(element);
  }, [dragRef]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  if (!usesDragHandle) {
    dragRef(ref);
  }

  return (
    <div
      {...divBaseProps}
      ref={ref}
      className={classNames(cn(), divBaseProps.className, {
        ['drag-disabled']: disabled,
        [cn('with-handle')]: usesDragHandle,
        [cn('dragging')]: isDragging,
        [cn('other-drag-in-progress')]: !isDragging && isGlobalDragging
      })}
    >
      {renderChildren(connectDragHandle)}
    </div>
  )
}
